/**
 * ActiveOverridesList — Phase 07.6.
 *
 * Surfaces in-house engine overrides on the review panel. Two render
 * modes driven by the panel's `mode` prop:
 *
 * - Read-only (anything other than `in_house_edit`): chips only, no
 *   affordances. Mirrors the inline pre-07.6 panel rendering so witness
 *   CPAs and read-only staff still see the override fingerprint without
 *   the ability to mutate it.
 * - Editable (`mode === 'in_house_edit'`): chips with an inactivate
 *   button on each + a header-level "Add override" button. Honors the
 *   per-row `disabled` state so the rerun-pending window doesn't let
 *   the reviewer pile new overrides on top of in-flight cascade work.
 *
 * Design intent (per docs/plans/2026-04-22-accountant-attestation/phases/phase-07-review-shell-card-retrofit/checklist.md §07.6):
 *   - Each active override has an "inactivate" button.
 *   - "Add override" button opens `OverrideFormModal` (see sibling).
 *   - Submit calls `addOverride` from Phase 06 endpoint (engineReviewApi).
 *
 * Accessibility intent (Phase 07.8):
 *   - The inactivate button has an aria-label that names the field.
 *   - The list is wrapped in a `data-testid="active-overrides"` region
 *     for the e2e regression net.
 */

import React from 'react';

import type { EngineOverride, ReviewPanelMode } from '../../../types/engineReview';

interface Props {
  overrides: EngineOverride[];
  mode: ReviewPanelMode;
  /** Disable interaction while a rerun is mid-cascade. */
  disabled?: boolean;
  /** Editable-mode callback fired when the reviewer clicks "X" on a chip.
   * Caller is responsible for collecting the inactivation reason and
   * routing it to `deactivateOverride`. */
  onInactivate?: (override: EngineOverride) => void;
  /** Editable-mode callback fired when the reviewer clicks "Add override".
   * Caller is responsible for opening `OverrideFormModal`. */
  onAddClick?: () => void;
}

const renderValue = (value: EngineOverride['override_value']): string => {
  if (value === null || value === undefined) return '—';
  return String(value);
};

export default function ActiveOverridesList({
  overrides,
  mode,
  disabled = false,
  onInactivate,
  onAddClick,
}: Props) {
  const isEditable = mode === 'in_house_edit';
  const hasOverrides = overrides.length > 0;

  // In read-only mode with no overrides, render nothing — the panel's
  // 07.5 contract is that empty optional sections collapse rather than
  // showing "0 overrides" placeholder noise.
  if (!isEditable && !hasOverrides) {
    return null;
  }

  return (
    <div className="px-4 pt-4" data-testid="active-overrides">
      <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5"
              style={{ color: 'var(--color-needs-human-text)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <span className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
              Active Overrides ({overrides.length})
            </span>
          </div>

          {isEditable && (
            <button
              type="button"
              onClick={onAddClick}
              disabled={disabled}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border rounded focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}
              data-testid="active-overrides-add"
              aria-label="Add a new override"
            >
              + Add override
            </button>
          )}
        </div>

        {hasOverrides ? (
          <div className="flex flex-wrap gap-2">
            {overrides.map((override) => (
              <span
                key={override.override_id}
                className="inline-flex items-center gap-1 px-2 py-1 border rounded text-xs"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                data-testid={`active-override-${override.field_id}`}
              >
                <span className="font-medium">{override.field_id}:</span>
                <span className="line-through" style={{ color: 'var(--color-text-ghost)' }}>
                  {renderValue(override.original_value)}
                </span>
                <span style={{ color: 'var(--color-needs-human-text)' }}>
                  {renderValue(override.override_value)}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => onInactivate?.(override)}
                    disabled={disabled}
                    className="ml-1 focus:outline-none focus:ring-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ color: 'var(--color-text-muted)' }}
                    data-testid={`active-override-inactivate-${override.field_id}`}
                    aria-label={`Inactivate override on ${override.field_id}`}
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          // Editable mode + no overrides: the empty-state copy keeps the
          // "Add override" button discoverable rather than collapsing
          // the whole region.
          <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
            No active overrides on this engine. Click "Add override" to
            replace a calculated field with a reviewer-supplied value.
          </p>
        )}
      </div>
    </div>
  );
}
